import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import "./sidebar.scss";
import { FaChevronDown, FaChevronRight } from "react-icons/fa";
import { HiMiniBars3 } from "react-icons/hi2";
import { LiaTimesSolid } from "react-icons/lia";
import { useDispatch, useSelector } from "react-redux";
import { useMenuItems } from "../../hooks/useMenuItems";
import {
  closeDashSidebarMenu,
  openDashSidebarMenu,
} from "../../features/slice/modalSlice";

const Sidebar = ({ panelTitle }) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const menuItems = useMenuItems();
  const [expandedSection, setExpandedSection] = useState(null);
  const [collapsed, setCollapsed] = useState(false);
  const { isDashSidebarCollapsed } = useSelector((state) => state.modal);

  // Load saved state from localStorage
  useEffect(() => {
    const savedSection = localStorage.getItem("expandedSection");
    const savedCollapsed = localStorage.getItem("sidebarCollapsed");
    if (savedSection) setExpandedSection(savedSection);
    if (savedCollapsed === "true") setCollapsed(true);
  }, []);

  // Persist expanded section
  useEffect(() => {
    localStorage.setItem("expandedSection", expandedSection || "");
  }, [expandedSection]);

  // Persist collapsed state
  useEffect(() => {
    localStorage.setItem("sidebarCollapsed", collapsed);
  }, [collapsed]);

  const handleDashSidebarMenu = () => {
    if (isDashSidebarCollapsed) {
      dispatch(closeDashSidebarMenu());
    } else {
      dispatch(openDashSidebarMenu());
    }
  };

  const toggleSection = (title) => {
    setExpandedSection((prev) => (prev === title ? null : title));
  };

  // Group menu items by highest role match
  const grouped = menuItems.reduce((acc, item) => {
    const group = item.roles.includes("Admin")
      ? "Admin"
      : item.roles.includes("Manager")
      ? "Manager"
      : item.roles.includes("Employee")
      ? "Employee"
      : "User";
    if (!acc[group]) acc[group] = [];
    acc[group].push(item);
    return acc;
  }, {});

  return (
    <div
      className={`sidebar-wrapper ${collapsed ? "collapsed" : ""} ${
        isDashSidebarCollapsed ? "open" : ""
      }`}
    >
      {/* Header with collapse + mobile toggle */}
      <div className="sidebar-bottom-margin">
        <div className="collapse-btn-wrapper">
          <h3 className="panel-title">{panelTitle}</h3>
          <button
            className="collapse-btn"
            onClick={() => setCollapsed((prev) => !prev)}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {collapsed ? "»" : "«"}
          </button>
          <button
            className="close-open-sidebar"
            onClick={handleDashSidebarMenu}
          >
            {isDashSidebarCollapsed ? <LiaTimesSolid /> : <HiMiniBars3 />}
          </button>
        </div>

        {/* Render grouped sections */}
        {Object.keys(grouped).map((group) => (
          <div key={group} className="sidebar-group">
            {/* {!collapsed && <div className="group-title">{group}</div>} */}
            {grouped[group].map(({ title, icon: Icon, path, items }) => {
              // check if a parent section should be active based on children
              const isParentActive = items?.some((i) =>
                location.pathname.startsWith(i.path)
              );

              return (
                <div key={title} className="sidebar-section">
                  {path ? (
                    // --- Top-level single links ---
                    <NavLink
                      to={path}
                      end // ✅ exact match only
                      className={({ isActive }) =>
                        `section-header single-link ${isActive ? "active" : ""}`
                      }
                      title={title}
                    >
                      <Icon className="section-icon" />
                      {!collapsed && <span>{title}</span>}
                    </NavLink>
                  ) : (
                    <>
                      {/* --- Collapsible parent section --- */}
                      <div
                        className={`section-header collapsible ${
                          expandedSection === title || isParentActive
                            ? "active"
                            : ""
                        }`}
                        onClick={() => toggleSection(title)}
                        title={title}
                      >
                        <Icon className="section-icon" />
                        {!collapsed && <span>{title}</span>}
                        {!collapsed && items && (
                          <span className="chevron-icon">
                            {expandedSection === title || isParentActive ? (
                              <FaChevronDown />
                            ) : (
                              <FaChevronRight />
                            )}
                          </span>
                        )}
                      </div>

                      {/* --- Child links --- */}
                      {items && (
                        <div
                          className={`section-items ${
                            expandedSection === title || isParentActive
                              ? "expanded"
                              : ""
                          }`}
                        >
                          {items.map(({ label, path: itemPath }) => (
                            <NavLink
                              key={label}
                              to={itemPath}
                              end
                              className={({ isActive }) =>
                                `sidebar-link ${isActive ? "active" : ""}`
                              }
                              title={label}
                            >
                              {!collapsed && label}
                            </NavLink>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};

export default Sidebar;